import React, { useMemo, useRef, useState, useCallback, useEffect } from "react";
import { formatContributionCurrency, getActivityContributionTotal, getActivityContributionValueTotal } from "../utils/activityContributions";
import { Ic, DAYS_HEB, btnSecondary } from "../components/UI";
import { HebrewCalendar } from "@hebcal/core";

/* ═══════════════════════════════════════════
   HELPERS
   ═══════════════════════════════════════════ */

function toHebrewDayLetters(num) {
  if (!Number.isFinite(num) || num < 1 || num > 30) return String(num || "");
  const units = ["", "א", "ב", "ג", "ד", "ה", "ו", "ז", "ח", "ט"];
  let letters = "";
  if (num <= 9) letters = units[num];
  else if (num === 10) letters = "י";
  else if (num === 11) letters = "יא";
  else if (num === 12) letters = "יב";
  else if (num === 13) letters = "יג";
  else if (num === 14) letters = "יד";
  else if (num === 15) letters = "טו";
  else if (num === 16) letters = "טז";
  else if (num === 17) letters = "יז";
  else if (num === 18) letters = "יח";
  else if (num === 19) letters = "יט";
  else if (num === 20) letters = "כ";
  else if (num <= 29) letters = `כ${units[num - 20]}`;
  else letters = "ל";
  return `${letters}׳`;
}

function hDateString(date) {
  try {
    const parts = new Intl.DateTimeFormat("he-IL-u-ca-hebrew", { weekday: "long", day: "numeric", month: "long", year: "numeric" }).formatToParts(date);
    const weekday = parts.find((p) => p.type === "weekday")?.value || "";
    const month = parts.find((p) => p.type === "month")?.value || "";
    const year = parts.find((p) => p.type === "year")?.value || "";
    const dayNum = Number(parts.find((p) => p.type === "day")?.value || 0);
    return `${weekday} ${toHebrewDayLetters(dayNum)} ${month} ${year}`.trim();
  } catch (_) { return date.toLocaleDateString("he-IL"); }
}

function hebrewDayMonth(date) {
  try {
    const parts = new Intl.DateTimeFormat("he-IL-u-ca-hebrew", { day: "numeric", month: "long" }).formatToParts(date);
    const month = parts.find((p) => p.type === "month")?.value || "";
    const dayNum = Number(parts.find((p) => p.type === "day")?.value || 0);
    return `${toHebrewDayLetters(dayNum)} ${month}`.trim();
  } catch (_) { return ""; }
}

function normalizeHebrewText(value) {
  return (value || "").toString().trim().toLowerCase().replace(/['"׳״]/g, "").replace(/\s+/g, " ");
}

function hebrewDateParts(date) {
  const parts = new Intl.DateTimeFormat("he-IL-u-ca-hebrew", { day: "numeric", month: "long", year: "numeric" }).formatToParts(date);
  return {
    dayNum: Number(parts.find((p) => p.type === "day")?.value || 0),
    month: parts.find((p) => p.type === "month")?.value || "",
    year: parts.find((p) => p.type === "year")?.value || "",
  };
}

function buildUpcomingHebrewDates(today = new Date(), daysAhead = 7) {
  const upcoming = [];
  for (let i = 0; i <= daysAhead; i += 1) {
    const d = new Date(today); d.setDate(today.getDate() + i);
    const h = hebrewDateParts(d);
    upcoming.push({ gregorianDate: d, hebrewDayLetters: normalizeHebrewText(toHebrewDayLetters(h.dayNum)), hebrewDayNum: normalizeHebrewText(String(h.dayNum)), hebrewMonth: normalizeHebrewText(h.month), hebrewYear: h.year });
  }
  return upcoming;
}

function toISODate(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function normalizeParashaTitle(hebrewTitle, englishTitle = "") {
  const raw = (hebrewTitle || "").trim();
  if (!raw) return englishTitle.replace(/^Parashat\s+/i, "").trim();
  return raw.replace(/^פ[^ ]*\s+/, "").trim();
}

/* ─── Style Constants ────────────────────────────────────────── */
const ACCENT = "#C41E3A";
const ACCENT_LIGHT = "#FFF1F3";
const BORDER = "#E5E7EB";
const BORDER_LIGHT = "#F3F4F6";
const TEXT_PRIMARY = "#1F2937";
const TEXT_SECONDARY = "#6B7280";
const TEXT_MUTED = "#9CA3AF";
const BG_SUBTLE = "#F9FAFB";
const BLUE = "#3B82F6";
const GREEN = "#16A34A";

/* ─── Inject Styles ──────────────────────────────────────────── */
if (typeof document !== "undefined") {
  const id = "journal-page-styles-v2";
  if (!document.getElementById(id)) {
    const s = document.createElement("style");
    s.id = id;
    s.textContent = `
      @keyframes jSheetUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
      @keyframes jSheetDown { from { transform: translateY(0); } to { transform: translateY(100%); } }
      @keyframes jFadeIn { from { opacity: 0; } to { opacity: 1; } }
      .j-cell-tap { -webkit-tap-highlight-color: transparent; user-select: none; }
      .j-cell-tap:active { transform: scale(0.95); }
      .j-nav-btn { transition: background .12s; -webkit-tap-highlight-color: transparent; }
      .j-nav-btn:active { background: #E5E7EB !important; }
      .j-event-card { transition: transform .1s; }
      .j-event-card:active { transform: scale(0.98); }
    `;
    document.head.appendChild(s);
  }
}

/* ═══════════════════════════════════════════
   MOBILE MONTH GRID (Google Calendar style)
   ═══════════════════════════════════════════ */
function MobileMonthGrid({ monthGrid, monthCursor, selectedDate, todayISO, eventCountByDate, onSelect }) {
  const curMonth = monthCursor.getMonth();

  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 1, padding: "0 2px" }}>
      {monthGrid.map((d) => {
        const iso = toISODate(d);
        const inMonth = d.getMonth() === curMonth;
        const isToday = iso === todayISO;
        const isSel = iso === selectedDate;
        const isShabbat = d.getDay() === 6;
        const evCount = eventCountByDate[iso] || 0;

        return (
          <button
            key={iso}
            className="j-cell-tap"
            onClick={() => onSelect(iso)}
            style={{
              display: "flex", flexDirection: "column", alignItems: "center",
              justifyContent: "center",
              padding: "8px 0 4px",
              border: "none",
              background: isSel ? ACCENT : "transparent",
              borderRadius: isSel ? 12 : 0,
              cursor: "pointer",
              opacity: inMonth ? 1 : 0.3,
              minHeight: 52,
              position: "relative",
              fontFamily: "inherit",
            }}
          >
            {/* Gregorian day */}
            <div style={{
              fontSize: 15, fontWeight: 700,
              color: isSel ? "white" : isToday ? BLUE : isShabbat ? ACCENT : TEXT_PRIMARY,
              lineHeight: 1.2,
              ...(isToday && !isSel ? {
                width: 28, height: 28, borderRadius: "50%",
                border: `2px solid ${BLUE}`,
                display: "flex", alignItems: "center", justifyContent: "center",
              } : {}),
            }}>
              {d.getDate()}
            </div>
            {/* Hebrew day - tiny */}
            <div style={{
              fontSize: 8, fontWeight: 500,
              color: isSel ? "rgba(255,255,255,0.8)" : TEXT_MUTED,
              marginTop: 1, lineHeight: 1,
            }}>
              {hebrewDayMonth(d).split(" ")[0]}
            </div>
            {/* Event dots */}
            {evCount > 0 && (
              <div style={{
                display: "flex", gap: 2, marginTop: 3,
                justifyContent: "center",
              }}>
                {evCount >= 1 && <span style={{ width: 4, height: 4, borderRadius: "50%", background: isSel ? "rgba(255,255,255,0.8)" : ACCENT }} />}
                {evCount >= 2 && <span style={{ width: 4, height: 4, borderRadius: "50%", background: isSel ? "rgba(255,255,255,0.6)" : BLUE }} />}
                {evCount >= 4 && <span style={{ width: 4, height: 4, borderRadius: "50%", background: isSel ? "rgba(255,255,255,0.4)" : "#F59E0B" }} />}
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
}

/* ═══════════════════════════════════════════
   DAY EVENTS PANEL
   ═══════════════════════════════════════════ */
function DayEventsPanel({ events, dateStr, hebrewDateStr, onAddActivity, isMobile }) {
  const chipColors = {
    activity:  { bg: "#EFF6FF", color: "#2563EB", border: "#BFDBFE", icon: "📋" },
    recurring: { bg: "#F0FDF4", color: GREEN, border: "#BBF7D0", icon: "🔁" },
    parasha:   { bg: ACCENT_LIGHT, color: ACCENT, border: "#FECDD3", icon: "✡️" },
  };

  return (
    <div>
      {/* Date header */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: isMobile ? "12px 16px" : "14px 20px",
        borderBottom: `1px solid ${BORDER_LIGHT}`,
      }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: TEXT_PRIMARY, fontFamily: "Assistant" }}>{dateStr}</div>
          <div style={{ fontSize: 12, color: TEXT_SECONDARY, marginTop: 2 }}>{hebrewDateStr}</div>
        </div>
        {typeof onAddActivity === "function" && (
          <button
            onClick={() => onAddActivity(dateStr)}
            style={{
              display: "flex", alignItems: "center", gap: 4,
              padding: "6px 12px", borderRadius: 8,
              background: "#EFF6FF", color: "#2563EB",
              border: "1px solid #BFDBFE", fontSize: 12,
              fontWeight: 600, fontFamily: "Heebo",
              cursor: "pointer",
            }}
          >
            <Ic.Plus /> הוסף
          </button>
        )}
      </div>

      {/* Events list */}
      <div style={{ padding: isMobile ? "8px 12px 16px" : "12px 20px 20px" }}>
        {events.length === 0 ? (
          <div style={{ textAlign: "center", padding: "28px 10px", color: TEXT_MUTED }}>
            <div style={{ fontSize: 28, marginBottom: 6 }}>🕊️</div>
            <div style={{ fontSize: 13 }}>אין אירועים ביום זה</div>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {events.map((ev, i) => {
              const cc = chipColors[ev.type] || chipColors.activity;
              return (
                <div key={`${ev.type}-${i}`} className="j-event-card" style={{
                  display: "flex", alignItems: "flex-start", gap: 10,
                  padding: "10px 12px", borderRadius: 10,
                  background: cc.bg, borderRight: `3px solid ${cc.color}`,
                }}>
                  <span style={{ fontSize: 16, flexShrink: 0, marginTop: 1 }}>{cc.icon}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: cc.color }}>{ev.title}</div>
                    <div style={{ fontSize: 12, color: TEXT_SECONDARY, marginTop: 2 }}>{ev.subtitle}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════ */
export default function JournalPage({ users, activities, volunteers, recurring, domains = [], isMobile = false, onAddActivity }) {
  const volMap = useMemo(() => { const m = {}; volunteers.forEach(v => m[v.id] = v); return m; }, [volunteers]);
  const domMap = useMemo(() => { const m = {}; domains.forEach(d => m[d.id] = d); return m; }, [domains]);

  const [monthCursor, setMonthCursor] = useState(() => { const now = new Date(); return new Date(now.getFullYear(), now.getMonth(), 1); });
  const [selectedDate, setSelectedDate] = useState(() => toISODate(new Date()));

  const todayISO = toISODate(new Date());
  const monthLabel = new Intl.DateTimeFormat("he-IL", { month: "long", year: "numeric" }).format(monthCursor);

  const hebcalCacheRef = useRef(new Map());

  const activitiesByDate = useMemo(() => {
    const m = {};
    activities.forEach(a => { const k = a.date || ""; if (!k) return; if (!m[k]) m[k] = []; m[k].push(a); });
    return m;
  }, [activities]);

  const recurringActive = recurring.filter(r => r.status === "active");

  const monthGrid = useMemo(() => {
    const y = monthCursor.getFullYear();
    const m = monthCursor.getMonth();
    const firstDay = new Date(y, m, 1);
    const offset = firstDay.getDay();
    const start = new Date(y, m, 1 - offset);
    return Array.from({ length: 42 }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return d; });
  }, [monthCursor]);

  const getHebcalDayData = useCallback((d) => {
    const key = toISODate(d);
    if (hebcalCacheRef.current.has(key)) return hebcalCacheRef.current.get(key);
    const start = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const end = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
    const dayEvents = HebrewCalendar.calendar({ start, end, il: true, sedrot: true, noModern: false, noMinorFast: false, noRoshChodesh: false });
    const parashaEvent = dayEvents.find(ev => (ev.getCategories?.() || []).includes("parashat"));
    const holidays = dayEvents.filter(ev => { const cats = ev.getCategories?.() || []; return cats.includes("holiday") && !cats.includes("parashat"); }).map(ev => ({ title: ev.render("he"), subtitle: ev.getDesc?.() || "" }));
    const data = { parasha: parashaEvent ? normalizeParashaTitle(parashaEvent.render("he"), parashaEvent.getDesc?.() || "") : "", holidays };
    hebcalCacheRef.current.set(key, data);
    return data;
  }, []);

  const eventsForDate = useCallback((d) => {
    const iso = toISODate(d);
    const events = [];
    const autoRecurringKeys = new Set(
      (activitiesByDate[iso] || [])
        .filter((a) => (a.source || "").trim() === "auto")
        .map((a) => {
          if ((a.recurringKey || "").trim()) return a.recurringKey.trim();
          if ((a.recurringId || "").trim()) return `${a.recurringId.trim()}_${iso}`;
          return "";
        })
        .filter(Boolean)
    );
    (activitiesByDate[iso] || []).forEach(a => {
      const volunteerName = volMap[(a.volunteerId || "").trim()]?.name || "";
      const domain = domMap[(a.domainId || "").trim()] || null;
      const domainName = domain?.name || "ללא תחום";
      const domainCity = (domain?.city || "").trim();
      const statusLabel = volunteerName ? volunteerName : "ממתין לשיוך";
      events.push({
        type: "activity",
        title: statusLabel,
        subtitle: `${domainName}${domainCity ? ` (${domainCity})` : ""} | ${a.time || "--:--"} | ${Number(a.duration || 0)} ש'${getActivityContributionTotal(a) > 0 ? ` | ${getActivityContributionTotal(a)} תרומות${getActivityContributionValueTotal(a) > 0 ? ` | ${formatContributionCurrency(getActivityContributionValueTotal(a))}` : ""}` : ""}`,
      });
    });
    recurringActive.filter(r => Number(r.day) === d.getDay()).forEach(r => {
      if (autoRecurringKeys.has(`${r.id}_${iso}`)) return;
      events.push({ type: "recurring", title: volMap[r.volunteerId]?.name || "ללא מתנדב", subtitle: `${domMap[r.domainId]?.name || "ללא תחום"} | ${r.time || "--:--"} | ${Number(r.duration || 0)} ש'` });
    });
    const hd = getHebcalDayData(d);
    if (hd.parasha) events.push({ type: "parasha", title: hd.parasha, subtitle: "שבת" });
    hd.holidays.forEach(h => events.push({ type: "parasha", title: h.title, subtitle: "חג/מועד" }));
    return events;
  }, [activitiesByDate, recurringActive, volMap, domMap, getHebcalDayData]);

  const eventCountByDate = useMemo(() => {
    const m = {};
    monthGrid.forEach(d => { const evs = eventsForDate(d); if (evs.length) m[toISODate(d)] = evs.length; });
    return m;
  }, [monthGrid, eventsForDate]);

  const selectedDateObj = new Date(selectedDate);
  const selectedDayEvents = eventsForDate(selectedDateObj);
  const selectedRecent = useMemo(() => [...activities].sort((a, b) => (b.date || "").localeCompare(a.date || "")).slice(0, 15), [activities]);

  /* Reminders */
  const upcomingHolidayItems = useMemo(() => {
    const seen = new Set(); const rows = []; const now = new Date();
    for (let i = 0; i <= 45; i++) {
      const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() + i);
      getHebcalDayData(d).holidays.forEach(h => {
        const key = `${toISODate(d)}_${h.title}`; if (seen.has(key)) return; seen.add(key);
        rows.push({ title: `חג: ${h.title}`, sub: `${toISODate(d)} | ${hDateString(d)}`, icon: "🕎" });
      });
    }
    return rows.slice(0, 8);
  }, [getHebcalDayData]);

  const upcomingBirthdays = useMemo(() => {
    const upcomingDates = buildUpcomingHebrewDates(new Date(), 7);
    return volunteers.filter(v => !!v.hebrewBirthday).map(v => {
      const hb = normalizeHebrewText(v.hebrewBirthday); if (!hb) return null;
      const match = upcomingDates.find(u => hb.includes(u.hebrewMonth) && (hb.includes(u.hebrewDayLetters) || hb.includes(u.hebrewDayNum)));
      return match ? { volunteer: v, matchDate: match.gregorianDate } : null;
    }).filter(Boolean).sort((a, b) => a.matchDate - b.matchDate);
  }, [volunteers]);

  const reminderItems = useMemo(() => [
    ...upcomingHolidayItems,
    ...upcomingBirthdays.slice(0, 8).map(({ volunteer, matchDate }) => ({ title: `🎂 ${volunteer.name}`, sub: `${volunteer.hebrewBirthday} | ${toISODate(matchDate)}`, icon: "🎂" })),
    ...recurringActive.slice(0, 8).map(r => ({ title: `${volMap[r.volunteerId]?.name || "ללא מתנדב"} - יום ${DAYS_HEB[r.day]}`, sub: `${domMap[r.domainId]?.name || ""} | ${r.time || "--:--"}`, icon: "🔁" })),
  ].slice(0, 10), [upcomingHolidayItems, upcomingBirthdays, recurringActive, volMap, domMap]);

  const prevMonth = () => setMonthCursor(p => new Date(p.getFullYear(), p.getMonth() - 1, 1));
  const nextMonth = () => setMonthCursor(p => new Date(p.getFullYear(), p.getMonth() + 1, 1));
  const goToday = () => { const now = new Date(); setMonthCursor(new Date(now.getFullYear(), now.getMonth(), 1)); setSelectedDate(toISODate(now)); };

  /* ─── Touch swipe for mobile month nav ─── */
  const touchRef = useRef(null);
  const handleTouchStart = (e) => { touchRef.current = e.touches[0].clientX; };
  const handleTouchEnd = (e) => {
    if (touchRef.current === null) return;
    const diff = touchRef.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 60) { diff > 0 ? nextMonth() : prevMonth(); }
    touchRef.current = null;
  };

  /* ═══════════════ MOBILE LAYOUT ═══════════════ */
  if (isMobile) {
    return (
      <div style={{ fontFamily: "'Heebo', 'Assistant', sans-serif", direction: "rtl", minHeight: "100vh", background: "#fff" }}>
        {/* ── Sticky top bar ─── */}
        <div style={{
          position: "sticky", top: 0, zIndex: 10, background: "white",
          borderBottom: `1px solid ${BORDER_LIGHT}`,
          padding: "10px 12px",
        }}>
          {/* Month nav */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
            <button className="j-nav-btn" onClick={nextMonth} style={{ border: "none", background: "transparent", padding: "8px 14px", borderRadius: 8, cursor: "pointer", fontSize: 14, fontWeight: 600, color: TEXT_SECONDARY, fontFamily: "inherit" }}>
              ‹ קודם
            </button>
            <button onClick={goToday} style={{ border: "none", background: "transparent", cursor: "pointer", fontFamily: "inherit", padding: 0 }}>
              <div style={{ fontSize: 17, fontWeight: 800, color: TEXT_PRIMARY, fontFamily: "Assistant" }}>{monthLabel}</div>
            </button>
            <button className="j-nav-btn" onClick={prevMonth} style={{ border: "none", background: "transparent", padding: "8px 14px", borderRadius: 8, cursor: "pointer", fontSize: 14, fontWeight: 600, color: TEXT_SECONDARY, fontFamily: "inherit" }}>
              הבא ›
            </button>
          </div>

          {/* Day headers */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 0 }}>
            {DAYS_HEB.map(d => (
              <div key={d} style={{ textAlign: "center", fontSize: 11, fontWeight: 700, color: TEXT_MUTED, padding: "4px 0" }}>{d}</div>
            ))}
          </div>
        </div>

        {/* ── Month grid (swipeable) ─── */}
        <div onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd} style={{ background: "white" }}>
          <MobileMonthGrid
            monthGrid={monthGrid}
            monthCursor={monthCursor}
            selectedDate={selectedDate}
            todayISO={todayISO}
            eventCountByDate={eventCountByDate}
            onSelect={setSelectedDate}
          />
        </div>

        {/* ── Selected day details (scrollable below) ─── */}
        <div style={{
          background: "white",
          borderTop: `3px solid ${ACCENT}`,
          borderRadius: "16px 16px 0 0",
          marginTop: 4,
          boxShadow: "0 -2px 12px rgba(0,0,0,0.04)",
          minHeight: 200,
        }}>
          <DayEventsPanel
            events={selectedDayEvents}
            dateStr={selectedDate}
            hebrewDateStr={hDateString(selectedDateObj)}
            onAddActivity={onAddActivity}
            isMobile={true}
          />

          {/* Reminders section */}
          {reminderItems.length > 0 && (
            <div style={{ padding: "0 12px 12px" }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: TEXT_PRIMARY, marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
                🔔 תזכורות קרובות
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {reminderItems.slice(0, 5).map((r, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", borderRadius: 8, background: BG_SUBTLE }}>
                    <span style={{ fontSize: 14, flexShrink: 0 }}>{r.icon}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: TEXT_PRIMARY, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.title}</div>
                      <div style={{ fontSize: 11, color: TEXT_MUTED }}>{r.sub}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recent activities */}
          {selectedRecent.length > 0 && (
            <div style={{ padding: "4px 12px 20px" }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: TEXT_PRIMARY, marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
                📋 פעולות אחרונות
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {selectedRecent.slice(0, 8).map(a => (
                  <div key={a.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", borderRadius: 8, background: BG_SUBTLE }}>
                    <span style={{ width: 5, height: 5, borderRadius: "50%", background: BLUE, flexShrink: 0 }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 500, color: TEXT_PRIMARY }}>{a.date} {a.time || ""}</div>
                      <div style={{ fontSize: 11, color: TEXT_MUTED }}>{Number(a.duration || 0)} שעות{getActivityContributionTotal(a) > 0 ? ` · ${getActivityContributionTotal(a)} תרומות` : ""}{getActivityContributionValueTotal(a) > 0 ? ` · ${formatContributionCurrency(getActivityContributionValueTotal(a))}` : ""}{a.notes ? ` · ${a.notes}` : ""}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  /* ═══════════════ DESKTOP LAYOUT ═══════════════ */
  return (
    <div style={{ fontFamily: "'Heebo', 'Assistant', sans-serif", direction: "rtl", minHeight: "calc(100vh - 170px)" }}>
      <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>

        {/* ──── Calendar ──── */}
        <div style={{ flex: 1, minWidth: 0, background: "white", borderRadius: 16, border: `1px solid ${BORDER}`, padding: 20, boxShadow: "0 1px 4px rgba(0,0,0,.03)" }}>
          {/* Header */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 38, height: 38, borderRadius: 10, background: ACCENT_LIGHT, display: "flex", alignItems: "center", justifyContent: "center" }}><Ic.Calendar /></div>
              <div style={{ fontSize: 22, fontWeight: 800, fontFamily: "Assistant", color: TEXT_PRIMARY }}>יומן חודשי</div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {typeof onAddActivity === "function" && (
                <button onClick={() => onAddActivity(selectedDate)} style={{ ...btnSecondary, borderRadius: 10, padding: "8px 14px", fontSize: 13, fontWeight: 700, background: "#EFF6FF", border: "1px solid #BFDBFE", color: "#1D4ED8" }}>
                  <Ic.Plus /> הוספת פעילות
                </button>
              )}
              <button onClick={goToday} style={{ ...btnSecondary, borderRadius: 10, padding: "8px 12px", fontSize: 12, fontWeight: 600 }}>היום</button>
              <div style={{ display: "flex", alignItems: "center", gap: 4, background: BG_SUBTLE, borderRadius: 10, border: `1px solid ${BORDER}`, padding: "4px 6px" }}>
                <button className="j-nav-btn" onClick={prevMonth} style={{ border: "none", background: "transparent", borderRadius: 8, padding: "6px 12px", cursor: "pointer", fontSize: 13, fontWeight: 600, color: TEXT_SECONDARY, fontFamily: "inherit" }}>‹ קודם</button>
                <div style={{ minWidth: 130, textAlign: "center", fontWeight: 700, fontSize: 15, color: TEXT_PRIMARY }}>{monthLabel}</div>
                <button className="j-nav-btn" onClick={nextMonth} style={{ border: "none", background: "transparent", borderRadius: 8, padding: "6px 12px", cursor: "pointer", fontSize: 13, fontWeight: 600, color: TEXT_SECONDARY, fontFamily: "inherit" }}>הבא ›</button>
              </div>
            </div>
          </div>

          {/* Day headers */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 6, marginBottom: 6 }}>
            {DAYS_HEB.map(d => (
              <div key={d} style={{ textAlign: "center", fontSize: 12, fontWeight: 700, color: TEXT_MUTED, padding: "6px 0" }}>{d}</div>
            ))}
          </div>

          {/* Grid */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 5 }}>
            {monthGrid.map(d => {
              const iso = toISODate(d);
              const inMonth = d.getMonth() === monthCursor.getMonth();
              const isSel = iso === selectedDate;
              const isToday = iso === todayISO;
              const isShabbat = d.getDay() === 6;
              const dayEvents = eventsForDate(d);
              const chipColors = {
                activity:  { bg: "#EDF4FF", color: "#2563EB", border: "#BFDBFE" },
                recurring: { bg: "#F0FDF4", color: GREEN, border: "#BBF7D0" },
                parasha:   { bg: ACCENT_LIGHT, color: ACCENT, border: "#FECDD3" },
              };

              return (
                <button
                  key={iso}
                  className="j-cell-tap"
                  onClick={() => setSelectedDate(iso)}
                  style={{
                    textAlign: "right",
                    border: isSel ? `2px solid ${ACCENT}` : isToday ? `2px solid ${BLUE}` : `1px solid ${BORDER_LIGHT}`,
                    background: isShabbat && inMonth ? "#FFFBFC" : inMonth ? "white" : BG_SUBTLE,
                    borderRadius: 10, minHeight: 108, padding: 7,
                    cursor: "pointer", opacity: inMonth ? 1 : 0.4,
                    fontFamily: "inherit", display: "flex", flexDirection: "column",
                    position: "relative", transition: "box-shadow 0.12s",
                  }}
                  onMouseEnter={e => e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,.08)"}
                  onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}
                >
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 3 }}>
                    <div style={{
                      fontSize: 14, fontWeight: 800,
                      color: isToday ? "white" : isShabbat ? ACCENT : TEXT_PRIMARY,
                      ...(isToday ? { background: BLUE, width: 26, height: 26, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" } : {}),
                    }}>{d.getDate()}</div>
                    <div style={{ fontSize: 10, color: TEXT_MUTED, whiteSpace: "nowrap" }}>{hebrewDayMonth(d)}</div>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 2, flex: 1 }}>
                    {dayEvents.slice(0, 3).map((ev, idx) => {
                      const cc = chipColors[ev.type] || chipColors.activity;
                      return (
                        <div key={idx} style={{ fontSize: 10, borderRadius: 5, padding: "2px 6px", background: cc.bg, color: cc.color, border: `1px solid ${cc.border}`, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", fontWeight: 500, lineHeight: 1.4 }}>
                          {ev.title}
                        </div>
                      );
                    })}
                    {dayEvents.length > 3 && <div style={{ fontSize: 9, color: TEXT_MUTED, fontWeight: 600 }}>+{dayEvents.length - 3} נוספים</div>}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* ──── Sidebar ──── */}
        <div style={{ width: 300, flexShrink: 0, display: "flex", flexDirection: "column", gap: 14 }}>
          {/* Day details */}
          <div style={{ background: "white", borderRadius: 14, border: `1px solid ${BORDER}`, overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,.03)" }}>
            <DayEventsPanel events={selectedDayEvents} dateStr={selectedDate} hebrewDateStr={hDateString(selectedDateObj)} onAddActivity={onAddActivity} isMobile={false} />
          </div>

          {/* Reminders */}
          <div style={{ background: "white", borderRadius: 14, border: `1px solid ${BORDER}`, padding: 16, boxShadow: "0 1px 4px rgba(0,0,0,.03)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
              <span style={{ fontSize: 16 }}>🔔</span>
              <span style={{ fontSize: 16, fontWeight: 800, fontFamily: "Assistant", color: TEXT_PRIMARY }}>תזכורות</span>
            </div>
            <div style={{ display: "flex", flexDirection: "column", maxHeight: 240, overflowY: "auto" }}>
              {reminderItems.map((r, i) => (
                <div key={i} style={{ padding: "8px", borderRadius: 8, display: "flex", gap: 8, alignItems: "flex-start" }}>
                  <span style={{ fontSize: 14, flexShrink: 0 }}>{r.icon}</span>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: TEXT_PRIMARY }}>{r.title}</div>
                    <div style={{ fontSize: 11, color: TEXT_MUTED }}>{r.sub}</div>
                  </div>
                </div>
              ))}
              {reminderItems.length === 0 && <div style={{ color: TEXT_MUTED, fontSize: 13, textAlign: "center", padding: 16 }}>אין תזכורות</div>}
            </div>
          </div>

          {/* Recent */}
          <div style={{ background: "white", borderRadius: 14, border: `1px solid ${BORDER}`, padding: 16, boxShadow: "0 1px 4px rgba(0,0,0,.03)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
              <span style={{ fontSize: 16 }}>📋</span>
              <span style={{ fontSize: 16, fontWeight: 800, fontFamily: "Assistant", color: TEXT_PRIMARY }}>פעולות אחרונות</span>
            </div>
            <div style={{ display: "flex", flexDirection: "column", maxHeight: 260, overflowY: "auto" }}>
              {selectedRecent.map(a => (
                <div key={a.id} style={{ padding: "8px", borderRadius: 8, display: "flex", gap: 8, alignItems: "flex-start" }}>
                  <span style={{ width: 5, height: 5, borderRadius: "50%", background: BLUE, flexShrink: 0, marginTop: 6 }} />
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 500, color: TEXT_PRIMARY }}>{a.date} {a.time || ""}</div>
                    <div style={{ fontSize: 11, color: TEXT_MUTED }}>{Number(a.duration || 0)} שעות{getActivityContributionTotal(a) > 0 ? ` · ${getActivityContributionTotal(a)} תרומות` : ""}{getActivityContributionValueTotal(a) > 0 ? ` · ${formatContributionCurrency(getActivityContributionValueTotal(a))}` : ""}{a.notes ? ` · ${a.notes}` : ""}</div>
                  </div>
                </div>
              ))}
              {selectedRecent.length === 0 && <div style={{ color: TEXT_MUTED, fontSize: 13, textAlign: "center", padding: 16 }}>אין פעולות להצגה</div>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
